import { useEffect, useRef } from 'react';
import type { TaskOverlap } from '@/utils/conflictDetection';
import type { DateBreach } from '@/utils/breachDetection';

const fmt = (d: Date): string =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

interface CombinedWarningDialogProps {
  breaches: DateBreach[];
  overlaps: TaskOverlap[];
  onProceed: () => void;
  onCancel: () => void;
  /** Only offered when there are overlapping tasks to shift. */
  onAutoResolve: () => void;
}

export const CombinedWarningDialog = ({
  breaches,
  overlaps,
  onProceed,
  onCancel,
  onAutoResolve,
}: CombinedWarningDialogProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  // Stable ref holds latest callbacks + one-time resolved guard (P3, P4).
  // Updating props directly in render keeps event handlers fresh without stale closures.
  const cbRef = useRef({ onCancel, onProceed, onAutoResolve, resolved: false });
  cbRef.current.onCancel = onCancel;
  cbRef.current.onProceed = onProceed;
  cbRef.current.onAutoResolve = onAutoResolve;

  // Reset the one-time guard each time a new dialog is presented (P3).
  useEffect(() => {
    cbRef.current.resolved = false;
  }, [breaches, overlaps]);

  const safeCall = (key: 'onCancel' | 'onProceed' | 'onAutoResolve') => {
    if (cbRef.current.resolved) return;
    cbRef.current.resolved = true;
    cbRef.current[key]();
  };

  useEffect(() => {
    if (breaches.length === 0 && overlaps.length === 0) return;

    cancelBtnRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        safeCall('onCancel');
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [breaches, overlaps]); // re-register on content change, not just count change (P4)

  if (breaches.length === 0 && overlaps.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) safeCall('onCancel'); }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="combined-dialog-title"
        aria-describedby="combined-dialog-desc"
        className="bg-white rounded-lg border-[3px] border-red-500 shadow-2xl p-6 max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="text-center mb-5">
          <div className="text-5xl mb-3" aria-hidden="true">⚠️</div>
          <h2 id="combined-dialog-title" className="text-xl font-bold text-gray-900">
            Planning Changes Detected
          </h2>
          <p id="combined-dialog-desc" className="mt-1 text-sm text-gray-600">
            Review the impacts below before confirming this move.
          </p>
        </div>

        {/* Breach section */}
        {breaches.length > 0 && (
          <section className="mb-5" aria-label="Delivery date breaches">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-red-700 uppercase tracking-wide mb-3">
              <span aria-hidden="true">🚨</span> Delivery Date Breaches
            </h3>
            {breaches.map((b, idx) => (
              <div
                key={idx}
                className="rounded-md border border-red-200 bg-red-50 p-4 mb-3 text-sm"
              >
                <p className="font-semibold text-gray-900 mb-2">{b.project.name}</p>
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-gray-700">
                  <dt className="font-medium">Committed date:</dt>
                  <dd>{fmt(b.committedDate)}</dd>
                  <dt className="font-medium">Current completion:</dt>
                  <dd>{fmt(b.currentEstimatedDate)}</dd>
                  <dt className="font-medium">New completion:</dt>
                  <dd>{fmt(b.newEstimatedDate)}</dd>
                  <dt className="font-medium">Days late:</dt>
                  <dd>
                    <span className="font-bold text-red-700">{b.daysBreach} working day{b.daysBreach !== 1 ? 's' : ''}</span>
                  </dd>
                </dl>
              </div>
            ))}
          </section>
        )}

        {/* Overlap section */}
        {overlaps.length > 0 && (
          <section className="mb-5" aria-label="Team member scheduling overlaps">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-orange-700 uppercase tracking-wide mb-3">
              <span aria-hidden="true">⚠️</span> Team Member Overlaps
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-warning-50 text-left">
                    <th className="px-3 py-2 font-semibold text-gray-700 border border-orange-200">Member</th>
                    <th className="px-3 py-2 font-semibold text-gray-700 border border-orange-200">Moved Task</th>
                    <th className="px-3 py-2 font-semibold text-gray-700 border border-orange-200">Conflicting Task</th>
                    <th className="px-3 py-2 font-semibold text-gray-700 border border-orange-200 whitespace-nowrap">Days</th>
                  </tr>
                </thead>
                <tbody>
                  {overlaps.map((c, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-warning-50/40'}>
                      <td className="px-3 py-2 border border-orange-200 font-medium text-gray-800">{c.teamMember.name}</td>
                      <td className="px-3 py-2 border border-orange-200 text-gray-700">{c.movedTask.name}</td>
                      <td className="px-3 py-2 border border-orange-200 text-gray-700">{c.conflictingTask.name}</td>
                      <td className="px-3 py-2 border border-orange-200 text-center font-semibold text-orange-700">{c.overlapDays}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-2 border-t border-gray-100">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={() => safeCall('onCancel')}
            className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
          >
            Cancel Changes
          </button>
          {overlaps.length > 0 && (
            <button
              type="button"
              onClick={() => safeCall('onAutoResolve')}
              className="px-4 py-2 rounded-md border border-blue-500 bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Auto-Resolve Overlaps
            </button>
          )}
          <button
            type="button"
            onClick={() => safeCall('onProceed')}
            className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600"
          >
            Proceed Anyway
          </button>
        </div>
      </div>
    </div>
  );
};
